import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AutoComplete } from 'antd'
import useI18n from '../../../../utils/useI18n'
import { useGoogleMapsPlaces } from '../../../../utils/googleMapsLoader'

export interface GooglePlacesFieldConfig {
  apiKey: string
  selectProps?: any
  autocompletionRequest?: google.maps.places.AutocompletionRequest
}

interface ValueType {
  city?: string | null
  lat?: number | null
  lng?: number | null
  formattedAddress?: string | null
  zipCode?: string | null
  country?: string | null
  countryIso?: string | null
  route?: string | null
  streetNumber?: string | null
  administrativeAreaLevels?: google.maps.GeocoderAddressComponent[] | null
}

export interface AntFormGooglePlacesProps {
  value?: ValueType | string
  onChange: (value: ValueType | string | undefined) => void
  config?: GooglePlacesFieldConfig
  renderFormattedAddress?: (result: google.maps.places.PlaceResult) => string
  autocompletionRequest?: google.maps.places.AutocompletionRequest
  editingMode?: boolean
  selectProps?: any
  allowFreeText?: boolean
}

const extractAddress = (
  result: google.maps.places.PlaceResult,
  renderFormattedAddress?: (result: google.maps.places.PlaceResult) => string
): ValueType => {
  const addressComponents = result.address_components ?? []

  return {
    formattedAddress: renderFormattedAddress
      ? renderFormattedAddress(result)
      : result.formatted_address ?? result.name ?? undefined,
    route: addressComponents.find(a => a.types.includes('route'))?.long_name,
    streetNumber: addressComponents.find(a => a.types.includes('street_number'))?.long_name,
    city: addressComponents.find(a => a.types.includes('locality'))?.long_name,
    zipCode: addressComponents.find(a => a.types.includes('postal_code'))?.long_name,
    lat: result.geometry?.location?.lat(),
    lng: result.geometry?.location?.lng(),
    administrativeAreaLevels: addressComponents.filter(a => a.types.some(e => e.includes('administrative_area_level'))),
    countryIso: addressComponents.find(a => a.types.includes('country'))?.short_name,
    country: addressComponents.find(a => a.types.includes('country'))?.long_name
  }
}

interface PredictionOption {
  value: string
  label: React.ReactNode
  placeId: string
}

export const AntFormGooglePlaces: React.FC<AntFormGooglePlacesProps> = (props: AntFormGooglePlacesProps) => {
  const { value, config, renderFormattedAddress, onChange, allowFreeText } = props
  const { selectProps = {}, apiKey, autocompletionRequest: configAutocompletionRequest } = config ?? {}
  const { t } = useI18n()
  const { isLoaded, loadError } = useGoogleMapsPlaces()

  const [inputValue, setInputValue] = useState<string>(() => {
    if (typeof value === 'string') return value
    return value?.formattedAddress ?? ''
  })
  const [options, setOptions] = useState<PredictionOption[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [googleReady, setGoogleReady] = useState<boolean>(
    typeof window !== 'undefined' && Boolean(window.google?.maps?.places)
  )

  const autocompleteService = useRef<google.maps.places.AutocompleteService>()
  const placesService = useRef<google.maps.places.PlacesService>()
  const searchDelay = useRef<number>()
  const mergedSelectProps = useMemo(
    () => ({
      ...(selectProps ?? {}),
      ...(props.selectProps ?? {})
    }),
    [selectProps, props.selectProps]
  )

  const mergedAutocompletionRequest = useMemo<Partial<google.maps.places.AutocompletionRequest>>(() => {
    const merged = {
      ...(configAutocompletionRequest ?? {}),
      ...(props.autocompletionRequest ?? {})
    }

    delete (merged as any).input
    return merged
  }, [configAutocompletionRequest, props.autocompletionRequest])

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) return

    autocompleteService.current = new window.google.maps.places.AutocompleteService()
    placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'))
    setGoogleReady(true)

    return () => {
      if (searchDelay.current) {
        window.clearTimeout(searchDelay.current)
      }
    }
  }, [isLoaded])

  useEffect(() => {
    if (typeof value === 'string') {
      setInputValue(value)
      return
    }

    if (value?.formattedAddress) {
      setInputValue(value.formattedAddress)
    }
  }, [value])

  if (!apiKey) {
    return (<div>API KEY must be provided to config</div>)
  }

  const handleSearch = (searchText: string): void => {
    mergedSelectProps?.onSearch?.(searchText)
    setInputValue(searchText)

    if (!searchText) {
      setOptions([])
      return
    }

    if (!googleReady || !autocompleteService.current) {
      return
    }

    if (searchDelay.current) window.clearTimeout(searchDelay.current)
    searchDelay.current = window.setTimeout(() => {
      setLoading(true)
      const predictionRequest: google.maps.places.AutocompletionRequest = {
        input: searchText,
        ...(mergedAutocompletionRequest ?? {}),
        types: mergedAutocompletionRequest?.types
      }

      autocompleteService.current?.getPlacePredictions(
        predictionRequest,
        (predictions, status) => {
          setLoading(false)
          if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
            setOptions([])
            return
          }

          setOptions(
            predictions.map(prediction => ({
              value: prediction.description,
              label: prediction.description,
              placeId: prediction.place_id
            }))
          )
        }
      )
    }, 250)
  }

  const handleSelect = (_selectedValue: string, option: PredictionOption): void => {
    mergedSelectProps?.onSelect?.(_selectedValue, option)

    if (!placesService.current || !option?.placeId) {
      onChange(undefined)
      return
    }

    setLoading(true)
    placesService.current.getDetails(
      {
        placeId: option.placeId,
        fields: ['address_components', 'geometry', 'formatted_address', 'name']
      },
      (result, status) => {
        setLoading(false)
        if (!result || status !== google.maps.places.PlacesServiceStatus.OK) {
          onChange(undefined)
          return
        }

        // Build from a clean slate so missing fields wipe previous ones
        const extracted = extractAddress(result, renderFormattedAddress)
        const nextValue: ValueType = {
          formattedAddress: extracted.formattedAddress ?? null,
          route: extracted.route ?? null,
          streetNumber: extracted.streetNumber ?? null,
          city: extracted.city ?? null,
          zipCode: extracted.zipCode ?? null,
          lat: extracted.lat ?? null,
          lng: extracted.lng ?? null,
          administrativeAreaLevels: extracted.administrativeAreaLevels ?? null,
          countryIso: extracted.countryIso ?? null,
          country: extracted.country ?? null
        }
        setInputValue(nextValue.formattedAddress ?? option.value)
        onChange(nextValue)
      }
    )
  }

  const handleClear = (): void => {
    mergedSelectProps?.onClear?.()
    setInputValue('')
    setOptions([])
    onChange(undefined)
  }

  const handleFreeTextCommit = (): void => {
    if (!allowFreeText) return
    const trimmed = inputValue?.trim() ?? ''
    onChange(trimmed.length > 0 ? trimmed : undefined)
  }

  return (
    <AutoComplete
      {...mergedSelectProps}
      value={inputValue}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={(val: string) => {
        mergedSelectProps?.onChange?.(val)
        setInputValue(val)
      }}
      onClear={handleClear}
      onBlur={(e) => {
        mergedSelectProps?.onBlur?.(e)
        handleFreeTextCommit()
      }}
      onKeyDown={(e) => {
        mergedSelectProps?.onKeyDown?.(e)
        if (allowFreeText && (e.key === 'Enter' || e.keyCode === 13)) {
          handleFreeTextCommit()
        }
      }}
      allowClear={mergedSelectProps?.allowClear ?? true}
      filterOption={false}
      placeholder={mergedSelectProps?.placeholder ?? t('sentences.search_location')}
      notFoundContent={mergedSelectProps?.notFoundContent ?? (inputValue ? t('sentences.no_address_found') : t('sentences.start_typing_to_search'))}
      loading={loading || !googleReady || mergedSelectProps?.loading}
      disabled={mergedSelectProps?.disabled || (!googleReady && !loadError)}
      dropdownStyle={{ ...mergedSelectProps?.dropdownStyle, zIndex: 2200 }}
    />
  )
}

AntFormGooglePlaces.displayName = 'AntFormGooglePlaces'

export default AntFormGooglePlaces
